import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Plus, Trash2 } from "lucide-react";
import { listContacts } from "@/lib/crmApi";
import { contactFullName } from "@/lib/contactName";

import { TL } from "@/lib/prekladyUI";
const RELATION_OPTIONS = [
  { value: "spouse",  label: "Manžel/ka" },
  { value: "partner", label: "Partner/ka" },
  { value: "child",   label: "Dítě" },
  { value: "parent",  label: "Rodič" },
  { value: "sibling", label: "Sourozenec" },
  { value: "other",   label: "Jiný" },
];

const RELATION_LABEL = RELATION_OPTIONS.reduce((acc, r) => { acc[r.value] = r.label; return acc; }, {});

// Robustnost: /api/family i /api/crm/contacts mohou vrátit ne-pole nebo null-položky.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/**
 * FamilyPicker — links other CRM contacts to the current one as family/household members.
 * Loads existing relations via GET /api/family/{contactId}; links via POST /api/household/link.
 *
 * v4.21 — TOKEN PASS: hardcoded gold/navy → CSS variable tokens (sage/indigo aware).
 */
export default function FamilyPicker({ contactId, initialMembers = [] }) {
  const [members, setMembers] = useState(() => asArr(initialMembers));
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picking, setPicking] = useState(null); // contact picked, awaiting relation choice
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false); // pro UI (disabled)
  const linkingRef = useRef(false); // synchronní zámek — dvojklik ve stejném ticku nepošle 2 requesty
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/family/${contactId}`);
      setMembers(asArr(data?.members));
      setError(false);
    } catch (e) {
      // 404 = ještě není domácnost → legitimní prázdno, ne chyba.
      // Jiná chyba (500/timeout/síť) → error stav, ať prázdno nevypadá jako „žádná rodina".
      if (e?.response?.status === 404) { setMembers([]); setError(false); }
      else setError(true);
    }
  }, [contactId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Přepnutí klienta → vyresetuj lokální stav, ať se neukáže rodina předchozího klienta
  // (a případný chybový/otevřený stav se nepřenese).
  useEffect(() => {
    setMembers(asArr(initialMembers));
    setError(false);
    setOpen(false);
    setPicking(null);
    setQuery("");
    setResults([]);
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const all = asArr(await listContacts());
        if (cancelled) return;
        const q = query.toLowerCase();
        const taken = new Set([contactId, ...asArr(members).map((m) => m.id)]);
        const matched = all.filter((c) =>
          !taken.has(c.id) && (contactFullName(c) || "").toLowerCase().includes(q)
        ).slice(0, 8);
        setResults(matched);
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [query, contactId, members]);

  const linkContact = async (relation) => {
    if (!picking || linkingRef.current) return; // dvojklik nesmí založit dvě vazby
    linkingRef.current = true;
    setLinking(true);
    try {
      await api.post("/api/household/link", {
        contact_id: contactId,
        related_to: picking.id,
        relation,
      });
      toast.success(`${contactFullName(picking)} přidán jako ${RELATION_LABEL[relation]}`);
      setPicking(null);
      setQuery("");
      setResults([]);
      setOpen(false);
      refresh();
      window.dispatchEvent(new CustomEvent("family-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nepodařilo se propojit");
    } finally {
      linkingRef.current = false;
      setLinking(false);
    }
  };

  // Vytvořit nového klienta s tímto jménem a rovnou ho propojit jako člena rodiny.
  // Užitečné, když poradce zatím ke jménu nemá kontakt v CRM.
  const createAndPick = async () => {
    const raw = query.trim();
    if (raw.length < 2 || linkingRef.current) return;
    linkingRef.current = true;
    setLinking(true);
    // Rozdělit křestní / příjmení (vše po první mezeře = příjmení)
    const sp = raw.indexOf(" ");
    const first = sp > 0 ? raw.slice(0, sp) : raw;
    const surname = sp > 0 ? raw.slice(sp + 1) : "";
    try {
      const { data } = await api.post("/api/crm/contacts", {
        name: first, surname, full_name: raw,
      });
      // Po vytvoření necháme uživatele vybrat vztah (stejný flow jako u existujícího)
      setPicking({ id: data?.id, name: data?.full_name || raw });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nepodařilo se vytvořit nového klienta");
    } finally {
      linkingRef.current = false;
      setLinking(false);
    }
  };

  const unlink = async (memberId, memberName) => {
    if (!window.confirm(`Odpojit ${memberName} z rodiny?`)) return;
    try {
      await api.delete(`/api/household/unlink/${memberId}`);
      toast.success("Odpojeno");
      refresh();
      window.dispatchEvent(new CustomEvent("family-changed"));
    } catch {
      toast.error("Nepodařilo se odpojit");
    }
  };

  return (
    <div className="space-y-2" data-testid="family-picker">
      {error && members.length === 0 ? (
        <div className="flex items-center justify-between gap-2 text-drobny py-1" data-testid="family-error" style={{ color: "var(--negative-text)" }}>
          <span>Rodinné vazby se nepodařilo načíst.</span>
          <button
            onClick={refresh}
            data-testid="family-retry"
            className="underline font-semibold flex-shrink-0"
            style={{ color: "var(--accent)" }}
          >
            Zkusit znovu
          </button>
        </div>
      ) : members.length === 0 ? (
        <div className="text-drobny py-1" style={{ color: "var(--muted)" }}>Žádné rodinné vazby zatím.</div>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => (
            <div
              key={m.id}
              className="group flex items-center justify-between px-2.5 py-1.5 rounded-lg"
              style={{ background: "var(--surface-2)", border: "1px solid var(--line)" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-drobny font-bold flex-shrink-0"
                  style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)", color: "var(--accent)" }}
                >
                  {(m.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs truncate" style={{ color: "var(--ink)" }}>{contactFullName(m)}</div>
                  <div className="text-popisek uppercase tracking-[0.14em]" style={{ color: "var(--muted)" }}>
                    {RELATION_LABEL[m.household_role] || m.household_role || "rodina"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => unlink(m.id, contactFullName(m))}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
                style={{ color: "var(--muted)" }}
                aria-label={`Odpojit ${contactFullName(m)}`}
                data-testid={`family-unlink-${m.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="family-add-btn"
          className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-drobny transition-all"
          style={{
            border: "1px dashed var(--accent)",
            background: "var(--accent-soft)",
            color: "var(--accent)",
          }}
        >
          <Plus size={12} /> Přidat člena rodiny
        </button>
      ) : (
        <div
          className="rounded-lg p-2.5 space-y-2"
          style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-card)" }}
        >
          {!picking ? (
            <>
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Hledat kontakt v CRM…"
                  autoFocus
                  className="w-full pl-7 pr-7 py-1.5 rounded-md text-xs outline-none transition-colors"
                  style={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--line)",
                    color: "var(--ink)",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--line)")}
                  data-testid="family-search-input"
                />
                <button aria-label={TL("Zavřít")}
                  onClick={() => { setOpen(false); setQuery(""); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5"
                  style={{ color: "var(--muted)" }}
                >
                  <X size={11} />
                </button>
              </div>
              {results.length > 0 && (
                <div className="space-y-0.5 max-h-40 overflow-y-auto">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setPicking(r)}
                      data-testid={`family-pick-${r.id}`}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-left transition-colors"
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-soft)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span className="text-xs truncate" style={{ color: "var(--ink)" }}>{contactFullName(r)}</span>
                      <span className="text-drobny truncate" style={{ color: "var(--muted)" }}>{r.phone || r.email || ""}</span>
                    </button>
                  ))}
                </div>
              )}
              {query.length >= 2 && results.length === 0 && (
                <button
                  onClick={createAndPick}
                  data-testid="family-create-new"
                  className="w-full flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-drobny transition-all"
                  style={{
                    border: "1px dashed var(--accent)",
                    background: "var(--accent-soft)",
                    color: "var(--accent)",
                  }}
                >
                  <Plus size={12} /> Vytvořit nového klienta „{query.trim()}“
                </button>
              )}
            </>
          ) : (
            <>
              <div className="text-drobny" style={{ color: "var(--muted)" }}>
                Vztah <span style={{ color: "var(--ink)" }}>{contactFullName(picking)}</span> →
              </div>
              <div className="grid grid-cols-2 gap-1">
                {RELATION_OPTIONS.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => linkContact(r.value)}
                    disabled={linking}
                    data-testid={`family-relation-${r.value}`}
                    className="px-2 py-1.5 rounded-md text-drobny transition-all disabled:opacity-50"
                    style={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--line)",
                      color: "var(--ink)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--accent-soft)";
                      e.currentTarget.style.borderColor = "var(--accent)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "var(--surface-2)";
                      e.currentTarget.style.borderColor = "var(--line)";
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPicking(null)}
                className="text-drobny transition-colors"
                style={{ color: "var(--muted)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted)")}
              >
                ← zpět
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
